import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "es-day-model col w-full h-full gap-10" }
const _hoisted_2 = { class: "row gap-06 w-full" }

import { DayModelContentResponse, DayModelLightResponse, DayModelResponse, PersonLightResponse, SettingsApi, TimeslotRequest, UpdateDayModelRequest } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import { ApiError, DateFormat, ItemSelect, ItemTag } from '@/models/share.model';
import { ref, watch, unref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EsCalendarDayModelContent from '@/components/calendar/esCalendarDayModelContent.vue';
import { DurationInput, EventClickArg, EventInput } from '@fullcalendar/core';
import { format } from 'date-fns';
import EsDayModelForm from '@/components/form/esDayModelForm.vue';
import EsTimeslotForm from '@/components/form/esTimeslotForm.vue';
import { DateClickArg } from '@fullcalendar/interaction';
import { useToast } from 'primevue';
import { toastError, toastSuccess } from '@/utils/toast-utils';
import { formatTime } from '@/utils/date-utils';
import EsNameForm from '@/components/form/esNameForm.vue';
import EsConfirmDialog from "@/components/modals/esConfirmDialog.vue";
import { useAppUserStore } from '@/stores/appUser';
import Select from 'primevue/select';

const UNAVAILABLE_LABEL = 'Indisponible';


export default /*@__PURE__*/_defineComponent({
  __name: 'esDayModelSettings',
  props: {
    selectedInstituteId: {},
    refreshData: { type: Boolean },
    selectedHealthPro: {}
  },
  emits: ['refreshed', 'dayModelChanged'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const { t } = useI18n();
const toast = useToast();
const appUserStore = useAppUserStore();

const settingsApi = new SettingsApi(medServerApiConfig);

const dayModels = ref<ItemSelect[]>([]);
const selectedDayModel = ref<ItemSelect>(null);
const refreshDayModel = ref<boolean>(props.refreshData);
const isEsDayModelFormOpened = ref<boolean>(false);
const isEsTimeslotFormOpened = ref<boolean>(false);
const displayDeleteButton = ref<boolean>(false);
const isSameDate = ref<boolean>(true);
const isNameFormOpened = ref<boolean>(false);
const isModaleDeleteOpened = ref<boolean>(false);
const selectedTimeSlot = ref<TimeslotRequest>({
  start: '',
  end: '',
  timeslotId: undefined
});
const selectedAppointmentType = ref<ItemSelect>(null);

const today = new Date();
const minTime = new Date(today);
minTime.setHours(0, 0, 0, 0);
const maxTime = new Date(today);
maxTime.setHours(24, 0, 0, 0);
const durationInput = ref<DurationInput>(null);

const emits = __emit;

const currentPersonId = computed(() => {
  if (!appUserStore.currentAppUserData || !appUserStore.currentAppUserData.healthGroupMemberships) {return null}
  return appUserStore.currentAppUserData.healthGroupMemberships[0].pwr.personWithRoleId
});

const transformToItemSelect = (dataToTransform: DayModelLightResponse): ItemSelect => {
    if (dataToTransform) {
      return {
        id: dataToTransform.dayModelId,
        label: dataToTransform.name
      }
    }
}

const findDayModelByInstitute = async () => {
  if (props.selectedInstituteId && (currentPersonId.value || props.selectedHealthPro?.id)) {
    const daysModelList: DayModelLightResponse[] =  await settingsApi.findDayModelByInstitute({instituteId: props.selectedInstituteId, doctorId: props.selectedHealthPro?.id ? props.selectedHealthPro.id : currentPersonId.value});
    dayModels.value = daysModelList.map((dayModel: DayModelLightResponse) => transformToItemSelect(dayModel));
  }
}

const changeDayModel = async (dayModel: ItemSelect) => {
  if (dayModel) {
      selectedDayModel.value = dayModel;
      refreshDayModel.value = true;

    } else {
      refreshDayModel.value = false;
    }
}

const openUpdateTimeslotForm = async (data : {info: EventClickArg, item: ItemTag}) => {
  selectedAppointmentType.value = data.item ?? null;

  if (data.info?.event) {
    selectedTimeSlot.value.timeslotId = data.info.event.id;
    selectedTimeSlot.value.start = format(data.info.event.start, DateFormat.TIME_LONG);
    selectedTimeSlot.value.end = format(data.info.event.end, DateFormat.TIME_LONG);
    displayDeleteButton.value = true;
    isEsTimeslotFormOpened.value = true;
  }
}

const openAddTimeslotForm = async (data : {info: DateClickArg, item: ItemTag}) => {
  selectedAppointmentType.value = data.item ?? null;

  if (data.info && data.info.date) {
    selectedTimeSlot.value.start = format(data.info.date, DateFormat.TIME_LONG);
    selectedTimeSlot.value.end = format(data.info.date, DateFormat.TIME_LONG);

    selectedTimeSlot.value.timeslotId = undefined;
    displayDeleteButton.value = false;
    isEsTimeslotFormOpened.value = true;
  }
}

const refreshAfterUpdate = (timeslotRequest: TimeslotRequest) => {
  if (timeslotRequest) {
    const dateToSplit = formatTime(timeslotRequest.start);
    const [hours, minutes] = dateToSplit.split(':').map(Number);

    durationInput.value = {
      hours: hours ,
      minutes: minutes
    };

    refreshDayModel.value = true;
    emits('dayModelChanged');
  }
}

const closeEsTimeslotFormModel = (timeslotRequest: TimeslotRequest) => {
  isEsTimeslotFormOpened.value = false;
  refreshAfterUpdate(timeslotRequest);  
}

const deleteTimeslot = async (timeslotRequest: TimeslotRequest) => {
  isEsTimeslotFormOpened.value = false;

  if (timeslotRequest?.timeslotId && selectedAppointmentType && selectedDayModel) {
    await settingsApi.deleteTimeslotDayModelById({instituteId: props.selectedInstituteId, daymodelId: selectedDayModel.value.id, timeslotId: timeslotRequest.timeslotId}).then(() => {
      refreshDayModel.value = true;
      emits('dayModelChanged');
      toastSuccess(toast, {
          summary: t('common.toast.success.delete'),
          detail:  t('settings.label.dayModel.form.message.success.timeslot')
      });
    }).catch((error: ApiError) => {
      if (error?.status) {
        toastError(toast, {
            summary: t('common.toast.error'),
            detail: t(`error.${error.status}`)
        });
      } else {
        toastError(toast, {
            summary: t('common.toast.error'),
            detail: t('error.DEFAULT')
        });
      }      
    });
  }
}

const openCreateDayModel = () => {
  isEsDayModelFormOpened.value = true;
}

const closeEsDayModelFormModal = async (dayModelCreated: DayModelLightResponse) => {
  isEsDayModelFormOpened.value = false;

  if (dayModelCreated) {
    findDayModelByInstitute();
      emits('dayModelChanged');
      selectedDayModel.value = transformToItemSelect(dayModelCreated);
  }
}

const executeRefreshData = async() => {
  await findDayModelByInstitute();

  emits('refreshed');
}

const openNameForm = () => {
    isNameFormOpened.value = true;
}

const updateNameMethod = async (nameForm: string) => {
  const updateRequest : UpdateDayModelRequest = {
        instituteId: props.selectedInstituteId,
        daymodelId : selectedDayModel.value.id,
        dayModelResquest: {
          name: unref(nameForm)
        }
      }

      await settingsApi.updateDayModel(updateRequest).then(() => {
        toastSuccess(toast, {
          summary: t('common.toast.success.update'),
          detail:  t('settings.label.dayModel.form.message.success.update')
        });
        closeNameFormModal(nameForm);
      }).catch((error: ApiError) => {
        toastError(toast, {
            summary: t('common.toast.error'),
            detail: error?.status ? t(`error.${error.status}`) : t('error.DEFAULT')
        });  
        
        // Throw error to set at false the var __alreadyClicked in v-one-click directive
        throw error;
      });
}

const closeNameFormModal = async (newName: string) => {
  isNameFormOpened.value = false;
  if (newName) {
    isNameFormOpened.value = false;
    selectedDayModel.value.label = unref(newName)
    emits('dayModelChanged');
  }
}

const openDeleteDayModel = () => {
  isModaleDeleteOpened.value = true;
}

const closeConfirmDelete = async (isConfirmed : boolean) => {
  if(isConfirmed){
     await settingsApi.deleteDayModel({instituteId: props.selectedInstituteId, daymodelId : selectedDayModel.value.id}).then(() => {
        toastSuccess(toast, {
          summary: t('common.toast.success.update'),
          detail:  t('settings.label.dayModel.form.message.success.delete')
        });
        isModaleDeleteOpened.value = false;
        emits('dayModelChanged');
        selectedDayModel.value = dayModels.value[0];
        findDayModelByInstitute();
      }).catch((error: ApiError) => {
        toastError(toast, {
            summary: t('common.toast.error'),
            detail: t(error?.status ? `error.${error.status}` : 'error.DEFAULT')
        });

        // Throw error to set at false the var __alreadyClicked in v-one-click directive
        throw t('error.DEFAULT');
      });
  } else {
    isModaleDeleteOpened.value = false;
  }
}

const transformToEventInput = (dayModelContent: DayModelContentResponse): EventInput => {
  return {
    id: dayModelContent.dayModelContentId,
    start: format(new Date(), DateFormat.ISO_SMALL) + ' ' + formatTime(dayModelContent.startTime),
    end:   format(new Date(), DateFormat.ISO_SMALL) + ' ' + formatTime(dayModelContent.endTime),
    title: formatTime(dayModelContent.startTime) + ' - ' + formatTime(dayModelContent.endTime),
    color: 'rgba(40, 40, 186, 0.72)'
  }
}

const getEventByDay = async (itemId: string) => {
  if (itemId && selectedDayModel.value && props.selectedInstituteId && (currentPersonId.value || props.selectedHealthPro?.id)) {
    return await settingsApi.getDayModelById({instituteId: props.selectedInstituteId, 
                                              daymodelId: selectedDayModel.value.id, 
                                              appointmentTypeId: itemId,
                                              doctorId: props.selectedHealthPro?.id ? props.selectedHealthPro.id : currentPersonId.value})
        .then((result:DayModelResponse) => {
          const res = result.dayModelContent.map((dayModelContent: DayModelContentResponse) => transformToEventInput(dayModelContent));
          return res;
      });
  }
  
}

const updateSlotDayModel = async (timeslotRequest: TimeslotRequest) => {
  if (timeslotRequest && selectedAppointmentType.value?.id && selectedDayModel.value?.id) {
      await settingsApi.addUpdateSlotDayModelById({instituteId: props.selectedInstituteId, daymodelId: selectedDayModel.value.id, appointmentTypeId: selectedAppointmentType.value.id, timeslotRequest: timeslotRequest}).then(() => {
        toastSuccess(toast, {
            summary: t('common.toast.success.update'),
            detail:  t('settings.label.dayModel.form.message.success.timeslot')
        });
         refreshAfterUpdate(timeslotRequest)
      }).catch((error: ApiError) => {
        toastError(toast, {
            summary: t('common.toast.error'),
            detail: error?.status ? t(`error.${error.status}`) : t('error.DEFAULT')
        });
      });
    }
}

const updateEvent = async(data: {id: string, start: string, end: string, selectedAppointmentType: ItemTag}) => {
  selectedAppointmentType.value = data.selectedAppointmentType;
  const timeslotRequest: TimeslotRequest = {
    timeslotId: data.id,
    start: format(new Date(data.start), DateFormat.TIME_LONG),
    end: format(new Date(data.end), DateFormat.TIME_LONG),
  };

  await updateSlotDayModel(timeslotRequest);
}

const setRefreshed = () => {
  refreshDayModel.value = false;
  emits('refreshed');
}

watch(
    () => props.selectedInstituteId,
  () => {
    selectedDayModel.value = undefined;
    dayModels.value = undefined;
    selectedTimeSlot.value = {
      start: '',
      end: '',
      timeslotId: undefined
    };
    selectedAppointmentType.value = undefined;
    executeRefreshData();
  }
);

watch(
  () => props.selectedHealthPro,
  () => {
    selectedDayModel.value = undefined;
    dayModels.value = undefined;
    selectedTimeSlot.value = {
      start: '',
      end: '',
      timeslotId: undefined
    };
    selectedAppointmentType.value = undefined;
    executeRefreshData();
  }
)

watch(
  () => props.refreshData,
  async (newVal) => {
    if (newVal) {
      selectedDayModel.value = dayModels.value[0];
      emits('refreshed');
    }
  }
);

onMounted(() => {
  selectedDayModel.value = undefined;
  dayModels.value = undefined;
  executeRefreshData();
});

return (_ctx: any,_cache: any) => {
  const _component_es_button_icon = _resolveComponent("es-button-icon")!
  const _component_es_button = _resolveComponent("es-button")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(Select), {
          modelValue: selectedDayModel.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedDayModel).value = $event)),
          options: dayModels.value,
          optionLabel: "label",
          size: "small",
          onValueChange: _cache[1] || (_cache[1] = ($event: any) => (changeDayModel($event))),
          class: "w-full"
        }, null, 8, ["modelValue", "options"]),
        _createVNode(_component_es_button_icon, {
          class: "mright-05 pad-m",
          icon: "pencil_fill",
          folder: "shareIcons",
          styles: "img-grey",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (openNameForm())),
          disabled: !selectedDayModel.value || selectedDayModel.value.label === UNAVAILABLE_LABEL
        }, null, 8, ["disabled"]),
        _createVNode(_component_es_button_icon, {
          class: "mright-05 pad-m",
          icon: "trash",
          folder: "settings",
          styles: "img-grey",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (openDeleteDayModel())),
          disabled: !selectedDayModel.value || selectedDayModel.value.label === UNAVAILABLE_LABEL
        }, null, 8, ["disabled"]),
        _createVNode(_component_es_button, {
          label: _unref(t)('action.add'),
          onClick: _cache[4] || (_cache[4] = ($event: any) => (openCreateDayModel())),
          "is-primary": false,
          "is-linked": false
        }, null, 8, ["label"])
      ]),
      (selectedDayModel.value)
        ? (_openBlock(), _createBlock(EsCalendarDayModelContent, {
            key: 0,
            selectedDayModel: selectedDayModel.value,
            selectedInstituteId: _ctx.selectedInstituteId,
            refreshData: refreshDayModel.value,
            selectedHealthPro: _ctx.selectedHealthPro,
            getEventByDay: getEventByDay,
            scroll: durationInput.value,
            onOpenUpdateTimeslotForm: _cache[5] || (_cache[5] = ($event: any) => (openUpdateTimeslotForm($event))),
            onOpenAddTimeslotForm: _cache[6] || (_cache[6] = ($event: any) => (openAddTimeslotForm($event))),
            onUpdateEvent: _cache[7] || (_cache[7] = ($event: any) => (updateEvent($event))),
            onRefreshed: _cache[8] || (_cache[8] = ($event: any) => (setRefreshed()))
          }, null, 8, ["selectedDayModel", "selectedInstituteId", "refreshData", "selectedHealthPro", "scroll"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(EsConfirmDialog, {
      isOpen: isModaleDeleteOpened.value,
      onModalClose: _cache[9] || (_cache[9] = () => closeConfirmDelete(false)),
      header: _unref(t)('settings.label.dayModel.form.header.delete'),
      message: _unref(t)('settings.label.dayModel.form.message.delete'),
      "on-confirm": () => closeConfirmDelete(true)
    }, null, 8, ["isOpen", "header", "message", "on-confirm"]),
    _createVNode(EsDayModelForm, {
      isOpen: isEsDayModelFormOpened.value,
      header: _unref(t)('settings.label.dayModel.form.header.add'),
      "selected-institute-id": _ctx.selectedInstituteId,
      "current-person-id": currentPersonId.value,
      "selected-health-pro-id": _ctx.selectedHealthPro?.id ?? null,
      onModalClose: _cache[10] || (_cache[10] = ($event: any) => (closeEsDayModelFormModal($event)))
    }, null, 8, ["isOpen", "header", "selected-institute-id", "current-person-id", "selected-health-pro-id"]),
    _createVNode(EsTimeslotForm, {
      isOpen: isEsTimeslotFormOpened.value,
      header: _unref(t)('settings.label.dayModel.form.header.timeslot'),
      start: _unref(formatTime)(selectedTimeSlot.value?.start),
      end: _unref(formatTime)(selectedTimeSlot.value?.end),
      timeslotId: selectedTimeSlot.value?.timeslotId,
      isSameDate: isSameDate.value,
      isSetting: false,
      min: _unref(minTime),
      max: _unref(maxTime),
      displayDeleteButton: displayDeleteButton.value,
      "selected-appointment-type-id": selectedAppointmentType.value?.id,
      "selected-day-model-id": selectedDayModel.value?.id,
      "selected-institute-id": _ctx.selectedInstituteId,
      onModalClose: _cache[11] || (_cache[11] = ($event: any) => (closeEsTimeslotFormModel($event))),
      onDeleteAction: _cache[12] || (_cache[12] = ($event: any) => (deleteTimeslot($event)))
    }, null, 8, ["isOpen", "header", "start", "end", "timeslotId", "isSameDate", "min", "max", "displayDeleteButton", "selected-appointment-type-id", "selected-day-model-id", "selected-institute-id"]),
    _createVNode(EsNameForm, {
      isOpen: isNameFormOpened.value,
      header: _unref(t)('settings.label.dayModel.form.header.name'),
      label: _unref(t)('settings.label.dayModel.form.name'),
      name: selectedDayModel.value?.label,
      "selected-institute-id": _ctx.selectedInstituteId,
      "selected-day-model-id": selectedDayModel.value?.id,
      updateMethod: updateNameMethod,
      onModalClose: _cache[13] || (_cache[13] = ($event: any) => (closeNameFormModal($event)))
    }, null, 8, ["isOpen", "header", "label", "name", "selected-institute-id", "selected-day-model-id"])
  ], 64))
}
}

})